import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import CustomApprovalModal from "@/shared/components/custom-modal";
import { PATH_PICK_DELIVERY_COMPANIES } from "@/features/pathpick/features/basket/constants/deliveryCompanies";
import { PathPickSmartStoreInfoResponse } from "@/features/pathpick/features/basket/types/response";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  smartStoreInfo: PathPickSmartStoreInfoResponse | null | undefined;
}

// 상품 정보 다운로드 요청 접수 완료 안내 모달 — 적용된 기본 정보 요약과 내역 이동 버튼 제공
function PathPickBasketDownloadCompleteModal({
  isOpen,
  onClose,
  smartStoreInfo,
}: Props) {
  const { t } = useI18nHelper();
  const navigate = useNavigate();

  const deliveryCompanyName = smartStoreInfo?.deliveryCompany
    ? PATH_PICK_DELIVERY_COMPANIES.find(
        (company) => company.code === smartStoreInfo.deliveryCompany
      )?.name ?? smartStoreInfo.deliveryCompany
    : undefined;
  const infoSummary = [
    smartStoreInfo?.afterServiceTelephoneNumber,
    deliveryCompanyName,
    smartStoreInfo?.deliveryType,
  ]
    .filter(Boolean)
    .join(" · ");

  // 상/하단 이미지는 선택 항목이라 한 장도 없으면 박스 자체를 감춘다.
  const imageSummary = [
    smartStoreInfo?.topImage?.name &&
      t("path_pick:basket.download_complete.top_image", {
        name: smartStoreInfo.topImage.name,
      }),
    smartStoreInfo?.bottomImage?.name &&
      t("path_pick:basket.download_complete.bottom_image", {
        name: smartStoreInfo.bottomImage.name,
      }),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // 화면보다 넓으면 왼쪽이 스크롤로도 닿지 않아 화면 폭으로 클램프한다.
      minWidth={"min(640px, 92dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick:basket.download_complete.title")}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"12px"}>
          <Text textStyle={"body-md-regular"} color={"#1D1D1B"}>
            {t("path_pick:basket.download_complete.body")}
          </Text>
          <Flex
            flexDirection={"column"}
            gap={"4px"}
            padding={"12px 16px"}
            borderRadius={"8px"}
            border={"0.5px solid #D4D4D1"}
            background={"#F7F9FA"}
          >
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {t("path_pick:basket.download_complete.info_title")}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {infoSummary}
            </Text>
          </Flex>
          {imageSummary && (
            <Flex
              flexDirection={"column"}
              gap={"4px"}
              padding={"12px 16px"}
              borderRadius={"8px"}
              border={"0.5px solid #D4D4D1"}
              background={"#F7F9FA"}
            >
              <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
                {t("path_pick:basket.download_complete.images_title")}
              </Text>
              <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                {imageSummary}
              </Text>
            </Flex>
          )}
        </Flex>
      </CustomApprovalModal.Body>
      <CustomApprovalModal.Footer
        cancelName={t("path_pick:basket.sync_complete.close")}
        confirmName={t("path_pick:basket.download_complete.go_to_history")}
        onCancel={onClose}
        onConfirm={() => navigate("/pathpick/sync-download-history")}
        confirmBtnProps={{ width: "200px" }}
      />
    </CustomApprovalModal>
  );
}

export default PathPickBasketDownloadCompleteModal;
